import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { selectIsAuthenticated, selectUser } from '../../../store/features/auth';
import { AuthPromptModal } from './AuthPromptModal';
import {
  applyAuthPromptGuardResult,
  evaluateAuthPromptGuard,
  resolveAuthPromptUserStatus,
} from './authPromptGuards';
import type { AuthPromptOpenOptions } from './authPrompt.types';

type AuthPromptContextValue = {
  openAuthPrompt: (options: AuthPromptOpenOptions) => void;
  closeAuthPrompt: () => void;
  isOpen: boolean;
};

const AuthPromptContext = createContext<AuthPromptContextValue | null>(null);

export function AuthPromptProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AuthPromptOpenOptions | null>(null);

  const closeAuthPrompt = useCallback(() => {
    setIsOpen(false);
    setOptions(null);
  }, []);

  const openAuthPrompt = useCallback(
    (next: AuthPromptOpenOptions) => {
      const guard = evaluateAuthPromptGuard(next, { isAuthenticated, user });
      const shouldShow = applyAuthPromptGuardResult(guard, navigate);
      if (!shouldShow) return;

      if (guard.type === 'show') {
        setOptions({
          ...guard.options,
          sourcePage: guard.options.sourcePage ?? location.pathname,
        });
        setIsOpen(true);
      }
    },
    [isAuthenticated, user, navigate, location.pathname],
  );

  const userStatus = resolveAuthPromptUserStatus({ isAuthenticated, user });

  const value = useMemo(
    () => ({ openAuthPrompt, closeAuthPrompt, isOpen }),
    [openAuthPrompt, closeAuthPrompt, isOpen],
  );

  return (
    <AuthPromptContext.Provider value={value}>
      {children}
      <AuthPromptModal
        isOpen={isOpen}
        options={options}
        userStatus={userStatus}
        onClose={closeAuthPrompt}
      />
    </AuthPromptContext.Provider>
  );
}

export function useAuthPrompt(): AuthPromptContextValue {
  const ctx = useContext(AuthPromptContext);
  if (!ctx) {
    throw new Error('useAuthPrompt must be used within AuthPromptProvider');
  }
  return ctx;
}

export function useAuthPromptOptional(): AuthPromptContextValue | null {
  return useContext(AuthPromptContext);
}

/** Run a protected action: executes immediately if authed, otherwise opens the prompt. */
export function useRequireAuth() {
  const { openAuthPrompt } = useAuthPrompt();

  return useCallback(
    (moment: AuthPromptOpenOptions['moment'], action: () => void | Promise<void>) => {
      openAuthPrompt({
        moment,
        onSuccess: () => void Promise.resolve(action()).catch(() => {
          toast.error('Something went wrong. Please try again.');
        }),
      });
    },
    [openAuthPrompt],
  );
}
